import {
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  IconButton,
  Select,
  SimpleGrid,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';

import React, { useEffect, useState } from 'react';
import Card from 'components/card/Card';
import BackButton from 'components/BackButton';
import { MdEdit } from 'react-icons/md';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import ProvidersTable from '../components/ProvidersTable';
import { createAPIEndPoint } from 'config/api/api';
import toast from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment';
import { logoutUser } from 'utils';
import { formatUSPhoneNumber } from 'utils';
import { toProperCase } from 'utils/formatting';

const locationData = [
  {
    name: 'John Doe',
    email: 'xyz@gmail.com',
    phone: '123456789',
    designation: 'Staff',
  },
];

const columnHelper = createColumnHelper();

export default function ProvidersDetails(props) {
  const navigate = useNavigate();
  const location = useLocation();
  // const { id } = location.state;
  const pathParts = location.pathname.split('/');
  const id = pathParts[pathParts.length - 1];
  const { ...rest } = props;
  // Columns definition
  const [provider, setProvider] = useState({});
  console.log('ProvidersDetails ~ provider:', provider);
  const [loading, setLoading] = useState(true);

  const getProvider = async () => {
    try {
      setLoading(true);
      const response = await createAPIEndPoint('clinic_providers').fetchById(
        `/${id}`,
      );
      const procedure = response.data.provider;

      setProvider(procedure);

      setLoading(false);
    } catch (error) {
      toast.error(
        error?.response?.data?.error || 'Error fetching provider details',
      );
      if (error?.response?.data?.error.includes('Bearer token')) {
        logoutUser(navigate);
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    getProvider();
  }, [id]);

  const columns = [
    columnHelper.accessor('name', {
      id: 'name',
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          NAME
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('email', {
      id: 'email',
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          Email
        </Text>
      ),
      cell: (info) => (
        <Text
          color={textColor}
          textTransform="none"
          fontSize="sm"
          fontWeight="700"
        >
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('phone', {
      id: 'phone',
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          Phone
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          {formatUSPhoneNumber(info.getValue()) || 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor('designation', {
      id: 'designation',
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          Designation
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('action', {
      id: 'action',
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          ACTION
        </Text>
      ),
      cell: (info) => {
        const handleEditClick = (e) => {
          onOpen();
          setType('edit');
        };

        return (
          <Box display="flex">
            <IconButton
              icon={<MdEdit style={{ marginLeft: '-1px' }} />}
              aria-label="Edit Details"
              colorScheme="brand"
              size="sm"
              mr="6px"
              onClick={handleEditClick}
            />
          </Box>
        );
      },
    }),
  ];

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [type, setType] = React.useState('add');
  const [sorting, setSorting] = React.useState([]);
  const [data, setData] = React.useState(() => [...locationData]);
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });
  // Chakra Color Mode
  const textColorPrimary = useColorModeValue('secondaryGray.900', 'white');
  const textColorSecondary = 'gray.400';
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const [isEnabled, setIsEnabled] = useState(true); // For status toggle

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <BackButton />
      {/* Main Fields */}
      <Grid
        templateColumns={{
          base: '1fr',
          lg: '1fr',
        }}
        templateRows={{
          base: 'repeat(2, 1fr)',
          lg: '1fr',
        }}
        gap={{ base: '20px', xl: '20px' }}
      >
        <Card mb={{ base: '0px', '2xl': '20px' }} {...rest}>
          <Text
            color={textColorPrimary}
            fontWeight="bold"
            fontSize="2xl"
            mt="8px"
            mb="24px"
          >
            General Information
          </Text>

          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
            <Box>
              <Text fontWeight="semibold" color={textColorPrimary}>
                Name:
              </Text>
              <Text color={textColorSecondary} fontSize="md">
                {`${provider.first_name} ${provider.last_name}` || 'N/A'}
              </Text>
            </Box>

            <Box>
              <Text fontWeight="semibold" color={textColorPrimary}>
                Email:
              </Text>
              <Text color={textColorSecondary} fontSize="md">
                {provider.email || 'N/A'}
              </Text>
            </Box>

            <Box>
              <Text fontWeight="semibold" color={textColorPrimary}>
                Phone:
              </Text>
              <Text color={textColorSecondary} fontSize="md">
                {formatUSPhoneNumber(provider.phone) || 'N/A'}
              </Text>
            </Box>

            <Box>
              <Text fontWeight="semibold" color={textColorPrimary}>
                Designation:
              </Text>
              <Text color={textColorSecondary} fontSize="md">
                {toProperCase(provider.designation) || 'N/A'}
              </Text>
            </Box>

            <Box>
              <Text fontWeight="semibold" color={textColorPrimary}>
                Provider code:
              </Text>
              <Text color={textColorSecondary} fontSize="md">
                {toProperCase(provider?.provider_code) || 'N/A'}
              </Text>
            </Box>

            <Box>
              <Text fontWeight="semibold" color={textColorPrimary}>
                NPI:
              </Text>
              <Text color={textColorSecondary} fontSize="md">
                {provider.npi || 'N/A'}
              </Text>
            </Box>

            <Box>
              <Text fontWeight="semibold" color={textColorPrimary}>
                TIN:
              </Text>
              <Text color={textColorSecondary} fontSize="md">
                {provider.tin || 'N/A'}
              </Text>
            </Box>

            <Box>
              <Text fontWeight="semibold" color={textColorPrimary}>
                State ID:
              </Text>
              <Text color={textColorSecondary} fontSize="md">
                {provider.state_id || 'N/A'}
              </Text>
            </Box>

            <Box>
              <Text fontWeight="semibold" color={textColorPrimary}>
                Status:
              </Text>
              {/* <Text color={textColorSecondary} fontSize="md">
                {provider.status === 1 ? 'Active' : 'Inactive'}
              </Text> */}

              <Badge
                colorScheme={provider?.status ? 'green' : 'red'}
                px={2}
                py={1.5}
                borderRadius="md"
                textAlign="center"
                minWidth="72px"
              >
                <Text fontSize="sm" textTransform="capitalize" fontWeight="700">
                  {/* {provider.status === 1 ? 'Active' : 'Inactive'} */}
                  {provider?.status || "N/A"}
                </Text>
              </Badge>
              {/* <Badge
                colorScheme={provider.status === 1 ? 'green' : 'red'}
                px={2}
                py={1.5}
                borderRadius="md"
                textAlign="center"
                minWidth="72px"
              >
                <Text fontSize="sm" textTransform="capitalize" fontWeight="700">
                  {provider.status === 1 ? 'Active' : 'Inactive'}
                </Text>
              </Badge> */}
            </Box>

            <Box>
              <Text fontWeight="semibold" color={textColorPrimary}>
                Created Date:
              </Text>
              <Text color={textColorSecondary} fontSize="md">
                {provider.created_at
                  ? moment(provider.created_at).format('MM/DD/YYYY')
                  : 'N/A'}
              </Text>
            </Box>
          </SimpleGrid>
        </Card>
      </Grid>
    </Box>
  );
}
